import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    NUMBER_SECRETS_STEP,
    NUMBER_TERMS_UPDATE_STEP,
    UPGRADE_ORDERED_STEPS
} from '@interlayer/js/app/components/main/Registration/components/templates/OnlineReg/components/RegInSteps/components/upgrade/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import {
    setAdditionalUpgradeData,
    setIsManuallyAddress,
    setRegistrationNameStep } from '@interlayer/js/app/store/reducers/Registration/actions';
import { STEP_NAMES } from '@interlayer/js/app/store/reducers/Registration/constants';

export const useUpgradeSteps = () => {
    const {
        forceToChangeEmail,
        additionalUpgradeData: { isConfirmedTerms }
    } = useRegistrationSelector();

    return useMemo(() => {
        const steps = [...UPGRADE_ORDERED_STEPS];

        if (isConfirmedTerms) {
            steps.splice(NUMBER_TERMS_UPDATE_STEP, 1);

            if (forceToChangeEmail) {
                steps.splice(NUMBER_SECRETS_STEP - 1, 1);
            }
        } else if (forceToChangeEmail) {
            steps.splice(NUMBER_SECRETS_STEP, 1);
        }

        return steps;
    }, [forceToChangeEmail, isConfirmedTerms]);
};

export const useUpgradeOrderSteps = () => {
    const upgradeSteps = useUpgradeSteps();

    return useMemo(() => {
        return upgradeSteps.reduce((acc: Partial<Record<STEP_NAMES, number>>, item, index) => {
            if (Array.isArray(item)) {
                item.forEach(step => {
                    acc[step] = index;
                });
            } else {
                acc[item] = index;
            }

            return acc;
        }, {});
    }, [upgradeSteps]);
};

export const useNextStep = () => {
    const upgradeSteps = useUpgradeSteps();
    const upgradeOrderSteps = useUpgradeOrderSteps();
    const { registrationNameStep } = useRegistrationSelector();

    const nextStep = upgradeSteps[(upgradeOrderSteps[registrationNameStep] || 0) + 1];

    if (Array.isArray(nextStep)) {
        return;
    }

    return nextStep;
};

export const usePrevStep = () => {
    const upgradeSteps = useUpgradeSteps();
    const upgradeOrderSteps = useUpgradeOrderSteps();
    const {
        isManuallyAddress,
        registrationNameStep,
        additionalUpgradeData: { isEditAddress }
    } = useRegistrationSelector();

    if (isManuallyAddress || isEditAddress) {
        return registrationNameStep;
    }

    const prevStep = upgradeSteps[(upgradeOrderSteps[registrationNameStep] || 1) - 1];

    if (Array.isArray(prevStep)) {
        return;
    }

    return prevStep;
};

export const useUpgradePagination = () => {
    const upgradeSteps = useUpgradeSteps();
    const dispatch = useDispatch();
    const nextStep = useNextStep();
    const prevStep = usePrevStep();
    const {
        isManuallyAddress,
        additionalUpgradeData: { isEditAddress }
    } = useRegistrationSelector();

    const handlerNextStep = useCallback(() => {
        if (!nextStep) {
            return;
        }

        dispatch(setRegistrationNameStep(nextStep));
    }, [dispatch, nextStep]);

    const handlerPrevStep = useCallback(() => {
        if (isManuallyAddress) {
            dispatch(setIsManuallyAddress(false));
            return;
        }

        if (isEditAddress) {
            dispatch(setAdditionalUpgradeData({ isEditAddress: false }));
            return;
        }

        if (!prevStep) {
            return;
        }

        dispatch(setRegistrationNameStep(prevStep));
    }, [dispatch, isEditAddress, isManuallyAddress, prevStep]);

    const handlerNavigateStep = useCallback((stepIndex) => {
        const stepName = upgradeSteps[stepIndex];

        if (Array.isArray(stepName)) {
            return;
        }

        dispatch(setRegistrationNameStep(stepName));
    }, [dispatch, upgradeSteps]);

    return useMemo(() => ({
        handlerNextStep,
        handlerPrevStep,
        handlerNavigateStep
    }), [handlerNavigateStep, handlerNextStep, handlerPrevStep]);
};
